import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

export default function HeaderUserMenu({
  user,
  extra,
}: {
  user: { name: string; avatarUrl?: string };
  /** Optional slot rendered at the top of the dropdown (e.g. the enterprise mode toggle). */
  extra?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { session } = useAuth();

  // Check if user is CLIENT_ADMIN
  const isClientAdmin = session?.role === "CLIENT_ADMIN";

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    if (open) {
      window.addEventListener("click", onClick);
      window.addEventListener("keydown", onEscape);
      return () => {
        window.removeEventListener("click", onClick);
        window.removeEventListener("keydown", onEscape);
      };
    }
  }, [open]);

  const handleSettings = () => {
    setOpen(false);
    navigate("/settings");
  };

  const handleSubscription = () => {
    setOpen(false);
    navigate("/subscription");
  };

  const handleSignOut = () => {
    setOpen(false);
    navigate("/logout");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center h-8 w-8 rounded-full bg-slate-200 text-slate-700 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#18365E]"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
      >
        {user.avatarUrl ? (
          <img 
            src={user.avatarUrl} 
            alt={user.name} 
            className="h-8 w-8 rounded-full object-cover" 
          />
        ) : (
          user.name[0]?.toUpperCase() || "U"
        )}
      </button>
      
      {open && (
        <div 
          role="menu" 
          className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-2 text-sm shadow-lg z-50"
        >
          {extra && (
            <>
              {/* Clicking the toggle flips mode + navigates; close the menu after. */}
              <div onClick={() => setOpen(false)}>{extra}</div>
              <hr className="my-2 border-slate-200" />
            </>
          )}
          <div className="px-2 py-1 text-slate-600 font-medium truncate">
            {user.name}
          </div>
          <hr className="my-2 border-slate-200" />
          <button
            onClick={handleSettings}
            className="block w-full rounded-lg px-2 py-1 text-left text-slate-700 hover:bg-slate-50 focus:outline-none focus:bg-slate-100"
            role="menuitem"
          >
            Settings
          </button>
          {isClientAdmin && (
            <button
              onClick={handleSubscription}
              className="block w-full rounded-lg px-2 py-1 text-left text-slate-700 hover:bg-slate-50 focus:outline-none focus:bg-slate-100"
              role="menuitem"
            >
              Subscription
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="mt-1 block w-full rounded-lg px-2 py-1 text-left text-red-600 hover:bg-red-50 focus:outline-none focus:bg-red-100"
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}